// src/pages/AdminNoticeRegister.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";

// rentCorpApi (사업자 조회)
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";

// parkingLotApi
import { useGetParkingLotByIdQuery, usePostParkingLotMutation, usePutParkingLotMutation } from "@/redux/parkingLotApi";

/**
 * AdminNoticeRegister
 * - 공지사항 등록 (CREATE) or 수정 (UPDATE)
 * - URL 파라미터: parkingId
 * - CREATE: postParkingLot
 * - UPDATE: getParkingLotById로 상세조회 → putParkingLot
 */
export const AdminNoticeRegister = () => {
    const { parkingId } = useParams();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,

        // 공지사항 필드
        name: "", // 주차장명
        rentCorpId: 0, // 사업자 지점
        totalArea: "", // 인가 면적
        totalCount: "", // 등록대수
        remainCount: "", // 잔여대수
        releaseCount: "", // 출고대수
        lessor: "", // 임대인
        lessorPhone: "", // 임대인 연락처
        officeUseYn: "Y", // 사무실 이용 여부
        bCode: "",
        address: "",
        addressDetail: "",
        parkingLotStatus: null,
        memo: "",
    });

    const { mode, toastMsg, confirmStatus, addressPopupStatus } = formData;

    // 1) 모드 설정: parkingId 유무
    useEffect(() => {
        if (parkingId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: parkingId,
            }));
        }
    }, [parkingId, setFormData]);

    // 2) UPDATE 모드일 경우, 공지사항 상세조회
    const {
        data: parkingLotDetail,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetParkingLotByIdQuery(parkingId, {
        skip: !parkingId, // parkingId 없으면 호출X
    });

    // 3) 상세조회 응답 → formData 매핑
    useEffect(() => {
        if (parkingLotDetail && parkingLotDetail.data) {
            const d = parkingLotDetail.data;
            setFormData((prev) => ({
                ...prev,
                name: d.name || "",
                rentCorpId: d.rentCorpId || 0,
                totalArea: d.totalArea || "",
                totalCount: d.totalCount || "",
                remainCount: d.remainCount || "",
                releaseCount: d.releaseCount || "",
                lessor: d.lessor || "",
                lessorPhone: d.lessorPhone || "",
                officeUseYn: d.officeUseYn || undefined,
                bCode: d.bCode || "",
                address: d.address || "",
                addressDetail: d.addressDetail || "",
            }));
        }
    }, [parkingLotDetail, setFormData]);

    // 4) 내 소속 렌트카 업체 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData?.data?.map((item) => ({
            value: item.id,
            label: item.corpName,
        })) || [];

    // 5) post / put mutation
    const [postParkingLot, { isLoading: isPostLoading }] = usePostParkingLotMutation();
    const [putParkingLot, { isLoading: isPutLoading }] = usePutParkingLotMutation();

    // 6) 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                name: formData.name,
                rentCorpId: formData.rentCorpId,
                totalArea: formData.totalArea,
                totalCount: formData.totalCount,
                remainCount: formData.remainCount,
                releaseCount: formData.releaseCount,
                lessor: formData.lessor,
                lessorPhone: formData.lessorPhone,
                officeUseYn: formData.officeUseYn,
                bCode: formData.bCode,
                address: formData.address,
                addressDetail: formData.addressDetail,
            };

            if (mode === "UPDATE") {
                // 수정
                await putParkingLot({
                    parkingLotId: formData.id,
                    body: requestBody,
                }).unwrap();
            } else {
                // 등록
                await postParkingLot(requestBody).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/erp/notice"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 7) 저장 버튼
    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `공지사항 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 8) 삭제 Confirm (미구현)
    const handleDeleteConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
            toastMsg: "삭제 기능은 미구현 상태입니다.",
        }));
    };

    // 9) 삭제 버튼
    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 공지사항 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "공지사항 등록" : "공지사항 수정"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    공지사항 삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                {mode === "CREATE" ? "등록" : "수정"}
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">공지사항 내용</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ParkingInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            corpOptions={corpOptions}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

// --------------------------------------------------------------------
// ParkingInfo Component
// --------------------------------------------------------------------
const ParkingInfo = ({ formData, handleChange, setFormData, corpOptions }) => {
    const {
        name,
        rentCorpId,
        totalArea,
        totalCount,
        remainCount,
        releaseCount,
        lessor,
        lessorPhone,
        officeUseYn,
        address,
        addressDetail,
        bCode,
        parkingLotStatus,
        memo,
    } = formData;
    const [value, setValue] = useState("");

    // 사무실 이용 여부 select options
    const officeUseOptions = [
        { value: "Y", label: "사용" },
        { value: "N", label: "미사용" },
    ];

    return (
        <>
            {/* 제목 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>제목</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="name" placeholder="제목" value={name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 활성/비활성 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>활성/비활성</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="campaign_type"
                                id="parkingLotStatus_DIRECT"
                                checked={parkingLotStatus === "DIRECT"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parkingLotStatus: "DIRECT",
                                    }));
                                }}
                            />
                            <label htmlFor="parkingLotStatus_DIRECT">활성화</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="campaign_type"
                                id="parkingLotStatus_SELECT"
                                checked={parkingLotStatus === "SELECT"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parkingLotStatus: "SELECT",
                                    }));
                                }}
                            />
                            <label htmlFor="parkingLotStatus_SELECT">비활성화</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 공지 내용 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>공지 내용</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <ReactQuill
                            theme="snow"
                            style={{ width: "100%", height: "400px" }}
                            value={value}
                            onChange={setValue}
                            placeholder="공지 내용을 작성해주세요."
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
